import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EditFormComponent } from 'app/form/edit-form/edit-form.component';
import { FormInstances } from 'app/form/form-instances.enum';
import { IFrameUrlKey } from 'app/shared/model/iframe-url-key.enum';
import { TemplateRouteContext } from 'app/shared/model/template-route-context.model';
import { RouteParamsService } from 'app/shared/service/route-params.service';

import { FormModalAction } from './form-modal-action.model';
import { FormModalResult } from './form-modal-result';

@Component({
  selector: 'cc-template-form-edit',
  template: '',
})
export class TemplateFormEditComponent implements OnInit, OnDestroy {
  public modalReference: NgbModalRef;
  private unSubscribe = new Subject<void>();
  private context: TemplateRouteContext;
  previousAction: FormModalAction;

  constructor(
    private modal: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private routeParamsService: RouteParamsService,
  ) {}

  ngOnInit(): void {
    this.context = this.route.snapshot.data.context;
    this.route.params.pipe(takeUntil(this.unSubscribe)).subscribe((params) => {
      const { scrollTop, reload, reason } = this.route.snapshot.queryParams;
      this.clearQueryParams();
      this.openFormModal({ formId: params.formId, scrollTop, reload, reason });
    });
  }

  private clearQueryParams() {
    void this.navigateTo({
      route: [],
      opts: {
        replaceUrl: true,
        queryParams: {
          formId: null,
          scrollTop: null,
          reload: null,
          reason: null,
        },
      },
    });
  }

  openFormModal(params: FormModalResult): void {
    const { reason, reload, formId, scrollTop } = params;
    const isInsideLibraryApps = this.router.url.includes(`/${IFrameUrlKey.LibraryApps}`);
    let dismissGuard;
    this.modalReference = this.modal.open(EditFormComponent, {
      backdrop: 'static',
      windowClass: 'form-modal full-modal',
      container: isInsideLibraryApps ? undefined : '.form-modal-container',
      keyboard: !isInsideLibraryApps,
      beforeDismiss: () => dismissGuard(),
    });
    const componentInstance = this.modalReference.componentInstance as EditFormComponent;
    dismissGuard = () => componentInstance.beforeDismiss();
    componentInstance.formInstance = FormInstances.EditForm;
    componentInstance.scrollTop = scrollTop;
    componentInstance.formId = formId;
    componentInstance.isLibraryView = this.context === TemplateRouteContext.Library;
    this.previousAction = reason;
    this.modalReference.result
      .then((result: FormModalResult) => {
        // eslint-disable-next-line promise/always-return
        if (this.previousAction === FormModalAction.Clone && result.reason !== FormModalAction.Clone) {
          void this.closeActions[FormModalAction.Close]({ reload: true, reason: FormModalAction.Clone });
        } else {
          void this.closeActions[result.reason]({ reload, formId, ...result });
        }
      })
      .catch(() => this.closeActions[FormModalAction.Close]({ reason, reload, formId }));
  }

  get closeActions() {
    return {
      [FormModalAction.Close]: (result: FormModalResult) => this.navigateTo({ opts: { state: result } }),
      [FormModalAction.Save]: (result: FormModalResult) =>
        this.navigateTo({ opts: { state: { ...result, reload: true } } }),
      [FormModalAction.Delete]: (result: FormModalResult) =>
        this.navigateTo({ opts: { state: { ...result, reload: true } } }),
      [FormModalAction.Clone]: (result: FormModalResult) =>
        this.navigateTo({ route: ['..', result.formId], opts: { queryParams: { ...result, reload: true } } }),
      [FormModalAction.Sign]: (result: FormModalResult) => this.openFormModal({ ...result, reload: true }),
      [FormModalAction.ResetSignature]: (result: FormModalResult) =>
        this.openFormModal({ ...result, reload: true }),
    };
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  navigateTo({ route = ['..'], opts = {} }) {
    return this.router.navigate(route, {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      ...opts,
    });
  }
}
